import { useState } from "react";
import { Check, CheckCheck, Clock, X, Smile, Languages, Loader2 } from "lucide-react";
import MediaViewer   from "./MediaViewer";
import LocationCard  from "./LocationCard";
import ContactCard   from "./ContactCard";
import ReactionPicker from "./ReactionPicker";
import { traduzirMensagem } from "../../services/mensagensApi";

/* ──────────────── helpers ──────────────── */

// Extrai texto seguro de um `conteudo` heterogêneo. Inbound da Meta tem
// `text: { body: "..." }`, outbound (que enviamos) tem `text: "..."` direto,
// alguns tipos têm `body`. Sempre devolve string — NUNCA objeto (evita
// React error #31 "object with keys {body}").
function extrairTexto(conteudo) {
  if (!conteudo) return "";
  if (typeof conteudo === "string") return conteudo;
  if (typeof conteudo.body === "string") return conteudo.body;
  if (typeof conteudo.text === "string") return conteudo.text;
  if (conteudo.text && typeof conteudo.text.body === "string") return conteudo.text.body;
  if (typeof conteudo.caption === "string") return conteudo.caption;
  return "";
}

function formatarHora(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

/* ──────────────── StatusIcon ──────────────── */

function StatusIcon({ status }) {
  // `status_meta` carrega os valores crus da Meta em inglês (accepted/sent/
  // delivered/read/failed) — tanto no GET quanto nos eventos de socket.
  // "pending" é a bolha otimista local; os valores em português cobrem o
  // status interno caso algum payload o traga no lugar.
  if (status === "pending" || status === "accepted")
    return <Clock      className="w-3 h-3 text-ink-4"    aria-label="Pendente" />;
  if (status === "sent" || status === "enviado")
    return <Check      className="w-3 h-3 text-ink-3"    aria-label="Enviado" />;
  if (status === "failed" || status === "falhou")
    return <X          className="w-3 h-3 text-red-400"  aria-label="Falhou" />;
  if (status === "delivered" || status === "entregue")
    return <CheckCheck className="w-3 h-3 text-ink-3"    aria-label="Entregue" />;
  if (status === "read" || status === "lido")
    return <CheckCheck className="w-3 h-3 text-blue-400" aria-label="Lido" />;
  return null;
}

/* ──────────────── ReacoesOverlay ──────────────── */

function ReacoesOverlay({ reacoes }) {
  if (!reacoes?.length) return null;
  const agrupadas = reacoes.reduce((acc, r) => {
    acc[r.emoji] = (acc[r.emoji] ?? 0) + 1;
    return acc;
  }, {});
  return (
    <div className="absolute -bottom-2.5 right-1 flex items-center gap-0.5 bg-surface-3 border border-white/[0.12] rounded-full px-1.5 py-0.5 shadow text-xs select-none">
      {Object.entries(agrupadas).map(([emoji, count]) => (
        <span key={emoji} className="leading-none" title={`${count}x`}>
          {emoji}{count > 1 && <sup className="text-[9px] text-ink-2">{count}</sup>}
        </span>
      ))}
    </div>
  );
}

/* ──────────────── InteractiveSnapshot ──────────────── */

function InteractiveSnapshot({ conteudo }) {
  const tipo = conteudo?.type ?? conteudo?.interactive_type ?? "";
  const body = conteudo?.body?.text ?? conteudo?.body ?? "";

  if (tipo === "button_reply") {
    return (
      <span className="text-xs text-brand-300 italic">
        Respondeu: {conteudo?.button_reply?.title ?? conteudo?.title ?? ""}
      </span>
    );
  }
  if (tipo === "list_reply") {
    return (
      <span className="text-xs text-brand-300 italic">
        Selecionou: {conteudo?.list_reply?.title ?? conteudo?.title ?? ""}
      </span>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      {body && <p className="text-xs text-ink-1 whitespace-pre-wrap">{body}</p>}
      <p className="text-xs text-ink-4 italic">[Mensagem interativa]</p>
    </div>
  );
}

/* ──────────────── TemplateSnapshot ──────────────── */

// Renderiza um template enviado mostrando o conteúdo real (header/body/
// footer/buttons) com os placeholders {{N}} substituídos pelos parâmetros.
// `templatesMap` é um Map name::lang → components (carregado em ChatView).
// Quando o template não está no cache (não aprovado ou removido), faz
// fallback pro snapshot simples "Template: <nome>".
function TemplateSnapshot({ conteudo, templatesMap }) {
  // O backend salva tanto `templateName/languageCode` (camelCase) quanto
  // `name/language` (formato Meta cru). Aceitamos os dois.
  const nome = conteudo?.templateName ?? conteudo?.name ?? conteudo?.template_name ?? "";
  const lang = conteudo?.languageCode
    ?? conteudo?.language?.code
    ?? conteudo?.language
    ?? "";

  const chave = `${nome}::${lang}`;
  const componentsTemplate = (templatesMap && templatesMap[chave]) || null;

  if (!componentsTemplate || componentsTemplate.length === 0) {
    return (
      <div className="flex flex-col gap-0.5">
        <p className="text-xs font-medium text-brand-300">Template: {nome}</p>
        {lang && <p className="text-xs text-ink-4">{lang}</p>}
      </div>
    );
  }

  // `conteudo.components` chega com `{ type: 'body', parameters: [{ text, type }] }`.
  const paramsBySection = {};
  for (const c of conteudo?.components || []) {
    const tipoSec = String(c?.type || "").toLowerCase();
    paramsBySection[tipoSec] = (c?.parameters || []).map((p) => p?.text ?? "");
  }

  function substituirPlaceholders(texto, params) {
    if (!texto || !params || params.length === 0) return texto || "";
    return texto.replace(/\{\{(\d+)\}\}/g, (_, n) => {
      const idx = parseInt(n, 10) - 1;
      return params[idx] != null ? params[idx] : `{{${n}}}`;
    });
  }

  const header  = componentsTemplate.find((c) => String(c?.type).toUpperCase() === "HEADER");
  const body    = componentsTemplate.find((c) => String(c?.type).toUpperCase() === "BODY");
  const footer  = componentsTemplate.find((c) => String(c?.type).toUpperCase() === "FOOTER");
  const buttons = componentsTemplate.find((c) => String(c?.type).toUpperCase() === "BUTTONS");

  const headerText = header?.format === "TEXT"
    ? substituirPlaceholders(header?.text, paramsBySection.header)
    : null;
  const bodyText = substituirPlaceholders(body?.text, paramsBySection.body);
  const footerText = footer?.text || "";

  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-[10px] uppercase tracking-wider text-brand-300/80">
        Template • {nome}
      </p>
      {headerText && (
        <p className="text-xs font-semibold text-ink-1">{headerText}</p>
      )}
      {bodyText && (
        <p className="text-xs text-ink-1 whitespace-pre-wrap leading-relaxed break-words">
          {bodyText}
        </p>
      )}
      {footerText && (
        <p className="text-[11px] text-ink-4 italic">{footerText}</p>
      )}
      {Array.isArray(buttons?.buttons) && buttons.buttons.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1 pt-1.5 border-t border-white/[0.06]">
          {buttons.buttons.map((b, i) => (
            <span
              key={i}
              className="text-[11px] text-brand-300 bg-brand-500/10 border border-brand-500/30 rounded px-2 py-0.5"
            >
              {b?.text || b?.type || "—"}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ──────────────── MessageBubble ──────────────── */

/**
 * Renderiza uma bolha de mensagem WhatsApp por tipo.
 *
 * @param {{
 *   mensagem: {
 *     id: number,
 *     wamid: string,
 *     tipo: string,
 *     conteudo: object,
 *     direcao: "in" | "out",
 *     status_meta: string,
 *     reacoes: Array<{wa_id: string, emoji: string, ts: string}>,
 *     data_criacao: string,
 *   },
 *   eu: boolean,
 *   contaId: number,
 *   conversaId: number,
 * }} props
 */
// Tipos que podem ter texto traduzível. Mensagens fora dessa lista
// (location, contacts, reaction, sticker, system) nunca mostram botão.
const TIPOS_TRADUZIVEIS = new Set([
  "text",
  "image",
  "video",
  "document",
  "button",
  "interactive",
]);

export default function MessageBubble({ mensagem, eu, contaId, conversaId, templatesMap }) {
  const [mostrarPicker, setMostrarPicker] = useState(false);
  // Tradução: hidrata do backend (texto_traduzido/idioma_detectado já podem
  // vir preenchidos por chamada anterior cacheada). Estado local sobrescreve
  // após clique do usuário.
  const [traducao, setTraducao] = useState({
    texto: mensagem.texto_traduzido || null,
    idioma: mensagem.idioma_detectado || null,
  });
  const [traduzindo, setTraduzindo] = useState(false);
  const [erroTraducao, setErroTraducao] = useState(null);

  const { tipo, conteudo, status_meta, reacoes, data_criacao } = mensagem;
  const wamid = mensagem.wamid ?? mensagem.wa_message_id ?? "";

  // Botão de tradução só aparece em mensagens INBOUND (recebidas), de tipos
  // que possam conter texto, e enquanto idioma_detectado não for 'pt'.
  // ID otimista (string `temp-...`) também esconde o botão — sem backend id.
  const podeTraduzir =
    !eu &&
    typeof mensagem.id === "number" &&
    TIPOS_TRADUZIVEIS.has(tipo) &&
    traducao.idioma !== "pt";

  async function handleTraduzir() {
    if (traduzindo || !podeTraduzir) return;
    setTraduzindo(true);
    setErroTraducao(null);
    try {
      const resp = await traduzirMensagem(mensagem.id);
      setTraducao({
        texto: resp?.texto_traduzido || null,
        idioma: resp?.idioma_detectado || null,
      });
    } catch (err) {
      const apiErr = err?.response?.data?.error;
      // Tipo sem texto traduzível: marca como pt-equivalente pra esconder.
      if (apiErr?.code === "INVALID_INPUT" || apiErr?.code === "NO_TEXT_TO_TRANSLATE") {
        setTraducao({ texto: null, idioma: "pt" });
      } else {
        setErroTraducao(apiErr?.message || "Falha ao traduzir.");
      }
    } finally {
      setTraduzindo(false);
    }
  }

  /* ── renderizar conteúdo por tipo ── */
  function renderConteudo() {
    switch (tipo) {
      case "text":
        return (
          <p className="text-xs text-ink-1 whitespace-pre-wrap leading-relaxed break-words">
            {extrairTexto(conteudo)}
          </p>
        );

      case "button": {
        // Reply a quick-reply de template/interactive: { button: { text, payload } }
        // Mensagens reclassificadas via SQL têm o button DENTRO de conteudo.raw.button
        // (worker antigo guardava o payload Meta cru em `raw`).
        const btnTexto =
          conteudo?.button?.text ??
          conteudo?.raw?.button?.text ??
          conteudo?.button?.payload ??
          conteudo?.raw?.button?.payload ??
          extrairTexto(conteudo) ??
          "—";
        return (
          <p className="text-xs text-brand-300 italic">
            Respondeu: <span className="text-ink-1 not-italic">{btnTexto}</span>
          </p>
        );
      }

      case "image":
      case "video":
      case "audio":
      case "document":
      case "sticker":
        return (
          <MediaViewer
            tipo={tipo === "sticker" ? "image" : tipo}
            mid={conteudo?.id ?? conteudo?.media_id}
            filename={conteudo?.filename ?? conteudo?.caption ?? undefined}
            caption={conteudo?.caption ?? undefined}
            contaId={contaId}
          />
        );

      case "location":
        return (
          <LocationCard
            latitude={conteudo?.latitude}
            longitude={conteudo?.longitude}
            name={conteudo?.name ?? undefined}
            address={conteudo?.address ?? undefined}
          />
        );

      case "contacts":
        return (
          <ContactCard
            contacts={conteudo?.contacts ?? conteudo ?? []}
          />
        );

      case "interactive":
        return <InteractiveSnapshot conteudo={conteudo} />;

      case "template":
        return <TemplateSnapshot conteudo={conteudo} templatesMap={templatesMap} />;

      case "reaction": {
        const emoji = conteudo?.emoji ?? "";
        const targetWamid = conteudo?.message_id ?? "";
        return (
          <p className="text-sm" title={`Reação a ${targetWamid}`} aria-label={`Reagiu com ${emoji}`}>
            {emoji}
          </p>
        );
      }

      case "system":
        return (
          <p className="text-xs text-ink-4 italic">
            {extrairTexto(conteudo) || "[Mensagem do sistema]"}
          </p>
        );

      default: {
        // Inbound desconhecido — tenta extrair texto, button ou fallback.
        // O worker incoming guarda o payload cru em `conteudo.raw` quando
        // não reconhece o tipo (ex: button reply). Tentamos exibir algo útil.
        const raw = conteudo?.raw || conteudo;
        const textoFallback =
          raw?.button?.text ??
          extrairTexto(raw) ??
          extrairTexto(conteudo);
        if (textoFallback) {
          return (
            <p className="text-xs text-ink-2 whitespace-pre-wrap break-words">
              {textoFallback}
            </p>
          );
        }
        return (
          <p className="text-xs text-ink-4 italic">
            [Tipo não suportado: {tipo}]
          </p>
        );
      }
    }
  }

  /* ── mensagem de sistema: centralizada, sem bolha ── */
  if (tipo === "system") {
    return (
      <div className="flex justify-center my-1 px-4" aria-label="Mensagem do sistema">
        <div className="bg-surface-2 border border-white/[0.08] rounded-full px-3 py-1 text-xs text-ink-3 text-center max-w-xs">
          {extrairTexto(conteudo) || "[Sistema]"}
        </div>
      </div>
    );
  }

  const alinhamento = eu ? "items-end" : "items-start";
  const corBolha    = eu ? "bg-brand-900 border-brand-800" : "bg-surface-2 border-white/[0.08]";

  return (
    <div className={`flex flex-col ${alinhamento} my-0.5 px-3 group`}>
      <div className="relative max-w-[72%]">
        {/* Bolha */}
        <div
          className={`relative rounded-xl border px-3 py-2 ${corBolha} shadow-sm`}
          role="article"
          aria-label={eu ? "Mensagem enviada" : "Mensagem recebida"}
        >
          {renderConteudo()}

          {/* Tradução: aparece abaixo do texto original quando o usuário
              clicou em "Traduzir" e a resposta voltou com texto pt-BR. */}
          {traducao.texto && (
            <div className="mt-1.5 pt-1.5 border-t border-white/[0.08]">
              <p className="text-[10px] uppercase tracking-wider text-brand-300/70 mb-0.5">
                Tradução {traducao.idioma ? `(${traducao.idioma} → pt)` : ""}
              </p>
              <p className="text-xs text-ink-1 whitespace-pre-wrap leading-relaxed break-words">
                {traducao.texto}
              </p>
            </div>
          )}

          {/* Erro de tradução: mensagem inline curta. */}
          {erroTraducao && (
            <p className="text-[10px] text-red-400 mt-1 italic" role="alert">
              {erroTraducao}
            </p>
          )}

          {/* Botão Traduzir: só em inbound de tipos com texto, enquanto
              idioma_detectado !== 'pt' E ainda não tem tradução cacheada. */}
          {podeTraduzir && !traducao.texto && (
            <button
              type="button"
              onClick={handleTraduzir}
              disabled={traduzindo}
              aria-label="Traduzir para português"
              title="Traduzir para português"
              className="mt-1 inline-flex items-center gap-1 text-[10px] text-brand-300 hover:text-brand-200 disabled:opacity-50 transition-colors focus:outline-none focus-visible:underline"
            >
              {traduzindo ? (
                <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />
              ) : (
                <Languages className="w-3 h-3" aria-hidden="true" />
              )}
              {traduzindo ? "Traduzindo…" : "Traduzir"}
            </button>
          )}

          {/* Hora + status */}
          <div className={`flex items-center gap-1 mt-1 ${eu ? "justify-end" : "justify-start"}`}>
            <span className="text-[10px] text-ink-4 leading-none select-none">
              {formatarHora(data_criacao)}
            </span>
            {eu && <StatusIcon status={status_meta} />}
          </div>

          {/* Reações */}
          <ReacoesOverlay reacoes={reacoes} />
        </div>

        {/* Botão de reação (aparece no hover) */}
        {wamid && conversaId && (
          <button
            type="button"
            aria-label="Reagir à mensagem"
            onClick={() => setMostrarPicker((v) => !v)}
            className={`absolute top-1 ${eu ? "-left-7" : "-right-7"} opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100 focus:outline-none p-1 rounded-full bg-surface-2 hover:bg-surface-3 border border-white/[0.08]`}
          >
            <Smile className="w-3.5 h-3.5 text-ink-3" />
          </button>
        )}

        {/* Picker de emoji */}
        {mostrarPicker && wamid && (
          <div className={`absolute ${eu ? "right-0" : "left-0"} top-8 z-20`}>
            <ReactionPicker
              conversaId={conversaId}
              messageId={wamid}
              onClose={() => setMostrarPicker(false)}
              onReact={() => setMostrarPicker(false)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
